import { Fragment, useMemo, useRef, useState } from 'react';
import { abortController } from '../utils/abortController';
import Grid from '@mui/material/Grid';
import VirtualizedAutocomplete from './Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';

// Files picked through the folder input carry "<folder>/<sub>/<name>" in webkitRelativePath
export const getFolderName = (file) => file?.webkitRelativePath ? file.webkitRelativePath.split("/")[0] : null;

// Keeps first-appearance order; single files stay as { item, index }, folder files collect under { folderName, entries }
const groupByFolder = (items, getItemFolderName) => items.reduce((groups, item, index) => {
    const folderName = getItemFolderName(item);

    if (!folderName) {
        groups.push({ item, index });
        return groups;
    }

    let group = groups.find(group => group.folderName === folderName);

    if (!group) {
        group = { folderName, entries: [] };
        groups.push(group);
    }

    group.entries.push({ item, index });
    return groups;
}, []);

const FileUploader = ({ isWritable = false, isLoading = false, isMultiple = true, isLocked = false, isKYC = false, customStyle = {}, uploadText = "", pathname = null, files = [], uploadedFiles = [], allowFileTypeSelection = false, kycFileTypes = [], buyingLegFileTypes = [], sellingLegFileTypes = [], allowZip = false, folderDownloadData = null, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, setFiles = () => { }, setUploadedFiles = () => { }, setDeletedRowFileIds = () => { }, triggerSessionExpire = () => { }, logOut = () => { } }) => {
    /* FILE VARIABLES */
    const maxFileSize = 10 * 1024 * 1024;
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const fileTypes = useMemo(() => allowZip ? ["pdf", "doc", "docx", "xlsx", "csv", "jpg", "jpeg", "png", "zip"] : ["pdf", "doc", "docx", "xlsx", "csv", "jpg", "jpeg", "png"], [allowZip]);
    const systemFileNames = ["thumbs.db", "desktop.ini"];
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const pendingFolderNames = [...new Set(uploadedFiles.map(getFolderName).filter(Boolean))];

    const isSupportedFileType = (file) => fileTypes.includes(file.name.split(".").pop()?.toLowerCase());

    // Folders carry OS junk (.DS_Store, ._ resource forks, __MACOSX, Thumbs.db) that the backend would reject
    const isHiddenOrSystemFile = (file) => {
        const pathSegments = (file.webkitRelativePath || file.name).split("/");
        return pathSegments.some(segment => segment.startsWith(".") || segment === "__MACOSX") || systemFileNames.includes(file.name.toLowerCase());
    }

    const addUploadedFiles = (newFiles) => {
        const uniqueFiles = new Set(uploadedFiles.map(file => (file.webkitRelativePath || file.name) + file.size));
        const newUploadedFiles = newFiles.filter(file => !uniqueFiles.has((file.webkitRelativePath || file.name) + file.size));

        // Files added to a folder that is already pending take on that folder's File Type
        newUploadedFiles.forEach(file => {
            const folderName = getFolderName(file);
            const folderFile = folderName && uploadedFiles.find(uploadedFile => getFolderName(uploadedFile) === folderName && uploadedFile.docType);

            if (folderFile) {
                file.docType = folderFile.docType;
            }
        });

        const allFiles = [...uploadedFiles, ...newUploadedFiles];
        const isMaxFileSizeExceeded = allFiles.reduce((total, file) => total + file.size, 0) > maxFileSize;

        if (isMaxFileSizeExceeded) {
            setAlertMessage(`The total file size exceeds the maximum allowed limit of ${maxFileSize / (1024 * 1024)} MB. Please reduce the file size or remove some files and try again.`);
            setAlertType("error");
            setShowAlert(true);
            return false;
        }

        setUploadedFiles(allFiles);
        return true;
    }

    const handleChange = (event) => {
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const newFiles = Object.values(event.target.files) || [];
        const isValidFileType = newFiles.every(isSupportedFileType);
        const isMultipleFiles = files.length === 1 || uploadedFiles.length === 1;

        if (!isMultiple && isMultipleFiles) {
            setAlertMessage("Please upload one file only.");
            setAlertType("error");
            setShowAlert(true);
        }
        else if (!isValidFileType) {
            setAlertMessage(`Please upload files with the following extensions: ${fileTypes.join(", ")}.`);
            setAlertType("error");
            setShowAlert(true);
        }
        else {
            addUploadedFiles(newFiles);
        }

        if (fileInputRef?.current?.value) {
            fileInputRef.current.value = null;
        }
    }

    const handleFolderChange = (event) => {
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const folderFiles = Object.values(event.target.files || {}).filter(file => !isHiddenOrSystemFile(file));
        const supportedFiles = folderFiles.filter(isSupportedFileType);
        const skippedFiles = folderFiles.filter(file => !isSupportedFileType(file));

        if (supportedFiles.length === 0) {
            setAlertMessage(`No supported files found in the selected folder. Allowed extensions: ${fileTypes.join(", ")}.`);
            setAlertType("error");
            setShowAlert(true);
        }
        else if (addUploadedFiles(supportedFiles) && skippedFiles.length > 0) {
            setAlertMessage(`${skippedFiles.length} unsupported file(s) were skipped: ${skippedFiles.map(file => file.webkitRelativePath || file.name).join(", ")}.`);
            setAlertType("warning");
            setShowAlert(true);
        }

        if (folderInputRef?.current?.value) {
            folderInputRef.current.value = null;
        }
    }

    const handleDeleteExistingFile = async (index, id) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        await axios({
            method: "post",
            url: "/api/DeleteDocument",
            data: {
                pathname: pathname,
                fileIds: [id]
            },
            signal: abortController.signal
        })
            .then((response) => {
                setIsLoading(false);
                const { status } = response;

                if (status === 200) {
                    const updatedFiles = [...files];
                    updatedFiles.splice(index, 1);

                    setFiles(updatedFiles);
                    setDeletedRowFileIds((previousDeletedFileIds) => [...previousDeletedFileIds, id]);
                    setAlertMessage("File Deleted Successfully.");
                    setAlertType("success");
                    setShowAlert(true);
                }
                else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => {
                console.log("Get Documents Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                    return;
                }

                const status = error?.response?.status;

                if (status === 403) {
                    triggerSessionExpire();
                }
                else {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                                : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);

                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                }
            });
    }

    const handleDeleteNewFile = (index) => {
        const newUploadedFiles = [...uploadedFiles];

        newUploadedFiles.splice(index, 1);
        setUploadedFiles(newUploadedFiles);
    }

    const handleDeleteNewFolder = (folderName) => {
        setUploadedFiles(uploadedFiles.filter(file => getFolderName(file) !== folderName));
    }

    const handleFolderTypeChange = (folderName, value) => {
        const updatedUploadedFiles = [...uploadedFiles];

        updatedUploadedFiles.forEach(file => {
            if (getFolderName(file) === folderName) {
                file.docType = value;
            }
        });

        setUploadedFiles(updatedUploadedFiles);
    }

    const handleToggleFolder = (folderKey) => {
        setExpandedFolders((previousExpandedFolders) => {
            const updatedExpandedFolders = new Set(previousExpandedFolders);

            if (updatedExpandedFolders.has(folderKey)) {
                updatedExpandedFolders.delete(folderKey);
            }
            else {
                updatedExpandedFolders.add(folderKey);
            }

            return updatedExpandedFolders;
        });
    }

    const handleDownloadFolder = async (folderName) => {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        await axios({
            method: "post",
            url: "/api/DownloadFolder",
            responseType: "blob",
            data: {
                pathname: pathname,
                ...folderDownloadData,
                Folder_Name: folderName
            },
            signal: abortController.signal
        })
            .then((response) => {
                setIsLoading(false);
                const { status, data, headers } = response;

                if (status === 200) {
                    const blob = new Blob([data], { type: data.type });
                    const blobURL = window.URL.createObjectURL(blob);

                    // Extract the filename from the Content-Disposition header
                    const contentDisposition = headers['content-disposition'];
                    let headerFileName = "";

                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

                        if (match != null && match[1]) {
                            headerFileName = match[1].replace(/['"]/g, '').trim();
                        }
                    }

                    const anchor = document.createElement("a");
                    anchor.download = headerFileName || `${folderName}.zip`;
                    anchor.href = blobURL;
                    anchor.dataset.downloadurl = [data.type, anchor.download, anchor.href].join(":");
                    anchor.click();

                    setTimeout(() => {
                        URL.revokeObjectURL(blobURL);
                    }, 100);
                }
                else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch(async (error) => {
                console.log("Download Folder Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                    return;
                }

                const status = error?.response?.status;

                if (status === 403) {
                    triggerSessionExpire();
                }
                else if (status === 404) {
                    // Error bodies arrive as a Blob because of responseType: "blob"
                    let message = "";

                    try {
                        const errorData = JSON.parse(await error.response.data.text());
                        message = errorData?.message || errorData?.error || "";
                    }
                    catch (parseError) {
                        message = "";
                    }

                    setAlertMessage(message || "No files were found in this folder.");
                    setAlertType("info");
                    setShowAlert(true);
                }
                else {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                                : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);

                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                }
            });
    }

    const handleTabTypeChange = (index, value) => {
        const updatedUploadedFiles = [...uploadedFiles];

        updatedUploadedFiles[index].tabType = value;
        updatedUploadedFiles[index].docType = null;

        setUploadedFiles(updatedUploadedFiles);
    }

    const handleSetMarkForApproval = (index, value) => {
        const updatedUploadedFiles = [...uploadedFiles];

        updatedUploadedFiles[index].markForApproval = value;
        setUploadedFiles(updatedUploadedFiles);
    }

    const handleUploadedFileChange = (index, value) => {
        const updatedUploadedFiles = [...uploadedFiles];

        updatedUploadedFiles[index].docType = value;
        setUploadedFiles(updatedUploadedFiles);
    }

    const handleViewFile = async (event, file) => {
        event.preventDefault();

        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const {
            dealPFIId,
            trancheId,
            cooId,
            name: fileName,
            location: fileBlobName,
            fileType,
            fileSource,
            status: fileStatus
        } = file.file;

        const fileRevisionNumber = isNaN(fileName?.value?.slice(1)) ? null : parseInt(fileName?.value?.slice(1), 10);

        await axios({
            method: "post",
            url: fileSource === "System Generated" ? "/api/ViewDocument" : "/api/AccessDocument",
            data: {
                ...(fileSource === "System Generated"
                    ? {
                        pathname: pathname,
                        Deal_PFI_Id: dealPFIId,
                        Tranche_Id: trancheId,
                        COO_ID: cooId,
                        Document_Type: fileType,
                        Revision_Number: fileRevisionNumber,
                        is_approved_document: fileStatus === "Approved",
                        view_document: true
                    }
                    : {
                        pathname: pathname,
                        FileBlobName: fileBlobName,
                        view_document: true
                    }
                )
            },
            signal: abortController.signal
        })
            .then((response) => {
                setIsLoading(false);
                const { status, data } = response;

                if (status === 200) {
                    window.open(data, "_blank", "noopener, noreferrer");
                }
                else if (status === 202) {
                    setAlertMessage(data?.data);
                    setAlertType("info");
                    setShowAlert(true);
                }
                else {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => {
                console.log("Download Document Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                    return;
                }

                const status = error?.response?.status;

                if (status === 403) {
                    triggerSessionExpire();
                }
                else {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                                : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);

                    if (status === 429) {
                        setTimeout(logOut, 3000);
                    }
                }
            });
    }

    const renderFolderRow = (folderKey, folderName, fileCount, fileTypeCell, actionsCell) => {
        const isExpanded = expandedFolders.has(folderKey);

        return (
            <Grid
                container
                className={`${allowFileTypeSelection ? 'file-table-content' : 'mt-2'}`}
            >
                <Grid size={{ xs: 5 }} className="d-flex align-items-center gap-1">
                    <IconButton
                        size="small"
                        aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
                        onClick={() => handleToggleFolder(folderKey)}
                    >
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                    <FolderIcon fontSize="small" sx={{ color: '#f4b400' }} />
                    <span className="uploaded-file">{folderName}</span>
                    <small>({fileCount} {fileCount === 1 ? "file" : "files"})</small>
                </Grid>

                {allowFileTypeSelection && (
                    <Grid size={{ xs: 5 }}>
                        {fileTypeCell}
                    </Grid>
                )}

                <Grid size={{ xs: 2 }}>
                    {actionsCell}
                </Grid>
            </Grid>
        );
    }

    const renderSavedFileRow = (file, index, isInFolder) => (
        <Grid
            key={`saved-${index}`}
            container
            className={`${allowFileTypeSelection ? 'file-table-content' : 'mt-2'}`}
        >
            <Grid size={{ xs: isKYC ? 5 : 2 }} sx={isInFolder ? { paddingLeft: '32px' } : undefined}>
                <a href="#/" onClick={(event) => handleViewFile(event, file)}>
                    <span className="uploaded-file cursor-pointer">
                        {file.file.name}
                    </span>
                </a>
            </Grid>

            {allowFileTypeSelection && (
                <>
                    {!isKYC && (
                        <>
                            <Grid size={{ xs: 2 }}>
                                <span>{file.file.fileSource}</span>
                            </Grid>

                            <Grid size={{ xs: 2 }}>
                                <span>{file.file.tabType}</span>
                            </Grid>
                        </>
                    )}

                    <Grid size={{ xs: isKYC ? 5 : 3 }}>
                        <span>{file.file.fileType}</span>
                    </Grid>

                    {!isKYC && (
                        <Grid
                            size={{ xs: 2 }}
                            sx={{
                                justifyContent: 'center',
                                color: file.file.fileSource === "System Generated"
                                    ? file.file.status === "Approved"
                                        ? '#00b242'
                                        : '#fe0000'
                                    : file.file.markedForApproval
                                        ? file.file.status
                                            ? '#00b242'
                                            : '#fe0000'
                                        : 'transparent'
                            }}
                        >
                            <span>
                                {file.file.markedForApproval ? (
                                    file.file.status ? "Approved" : "Not Approved"
                                ) : (
                                    file.file.fileSource === "System Generated"
                                        ? file.file.status
                                        : "Approval Not Required"
                                )}
                            </span>
                        </Grid>
                    )}
                </>
            )}

            <Grid size={{ xs: isKYC ? 2 : 1 }}>
                {file.file.fileSource === "Uploaded File" && (
                    <IconButton
                        color="error"
                        size="small"
                        disabled={!isWritable || isLocked}
                        onClick={() => handleDeleteExistingFile(index, file.file.id)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );

    const renderPendingFileRow = (file, index, isInFolder) => (
        <Grid
            key={`pending-${index}`}
            container
            className={`${allowFileTypeSelection ? 'file-table-content' : 'mt-2'}`}
        >
            <Grid size={{ xs: isKYC ? 5 : 2 }} sx={isInFolder ? { paddingLeft: '32px' } : undefined}>
                <span className="uploaded-file cursor-pointer">
                    {file.name}
                </span>
            </Grid>

            {allowFileTypeSelection && (
                isKYC ? (
                    <Grid size={{ xs: 5 }}>
                        {isInFolder ? (
                            // Folder files share the File Type chosen on the folder row
                            <span>{file.docType?.value || ""}</span>
                        ) : (
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isRequired={true}
                                isWritable={true}
                                filterOn="File Type"
                                options={kycFileTypes || []}
                                selectedOptions={file.docType || null}
                                handleSelectChange={(filterOn, newValue) => handleUploadedFileChange(index, newValue)}
                            />
                        )}
                    </Grid>
                ) : (
                    <>
                        <Grid size={{ xs: 2 }}>
                            <span>Uploaded File</span>
                        </Grid>

                        <Grid size={{ xs: 2 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={false}
                                isRequired={true}
                                isWritable={true}
                                filterOn="Tab"
                                options={["Buying Leg", "Selling Leg"]}
                                selectedOptions={file.tabType || null}
                                handleSelectChange={(filterOn, newValue) => handleTabTypeChange(index, newValue)}
                            />
                        </Grid>

                        <Grid size={{ xs: 3 }}>
                            <VirtualizedAutocomplete
                                isMultiple={false}
                                isObject={true}
                                isDisabled={!file.tabType}
                                isRequired={true}
                                isWritable={true}
                                filterOn="File Type"
                                options={file.tabType === "Buying Leg" ? buyingLegFileTypes : file.tabType === "Selling Leg" ? sellingLegFileTypes : []}
                                selectedOptions={file.docType || null}
                                handleSelectChange={(filterOn, newValue) => handleUploadedFileChange(index, newValue)}
                            />
                        </Grid>

                        <Grid size={{ xs: 2 }}>
                            <FormControlLabel
                                label="Mark for Approval"
                                sx={{
                                    margin: 'auto',
                                    gap: '3px'
                                }}
                                control={
                                    <Checkbox
                                        size="small"
                                        sx={{
                                            padding: 0,
                                            marginTop: '-1px'
                                        }}
                                        checked={file?.markForApproval || false}
                                        onChange={(event) => handleSetMarkForApproval(index, event.target.checked)}
                                    />
                                }
                            />
                        </Grid>
                    </>
                )
            )}

            <Grid size={{ xs: isKYC ? 2 : 1 }}>
                <IconButton
                    color="error"
                    size="small"
                    disabled={!isWritable}
                    onClick={() => handleDeleteNewFile(index)}
                >
                    <DeleteIcon fontSize="inherit" />
                </IconButton>
            </Grid>
        </Grid>
    );

    return (
        <div className="upload-container my-3 w-100">
            <Grid
                container
                spacing={"10px"}
                className={`w-100 flex-column`}
            >
                {isKYC && (
                    <Grid size={{ xs: 12 }} className="form-text-field w-fit">
                        <small className="page-heading title">
                            {uploadText}
                        </small>
                    </Grid>
                )}

                <Grid size={{ xs: 4 }} className="pt-0">
                    <div className="form-group">
                        <input
                            ref={fileInputRef}
                            className="form-control"
                            type="file"
                            name="documents"
                            multiple={isMultiple}
                            disabled={!isWritable}
                            placeholder="Choose File"
                            accept={fileTypes.map(type => `.${type}`).join(", ")}
                            onChange={handleChange}
                        />
                    </div>
                </Grid>

                {isKYC && isMultiple && (
                    <Grid size={{ xs: 4 }} className="pt-0">
                        <div className="form-group d-flex align-items-center gap-2">
                            {/* Native folder input is hidden because the browser labels it "Choose files" */}
                            <input
                                ref={folderInputRef}
                                className="d-none"
                                type="file"
                                name="documentsFolder"
                                webkitdirectory=""
                                directory=""
                                multiple
                                disabled={!isWritable}
                                onChange={handleFolderChange}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FolderIcon />}
                                disabled={!isWritable}
                                onClick={() => folderInputRef?.current?.click()}
                            >
                                Choose folder
                            </Button>
                            <small className="text-truncate">
                                {pendingFolderNames.length > 0 ? pendingFolderNames.join(", ") : "No folder chosen"}
                            </small>
                        </div>
                    </Grid>
                )}

                {/* {isLoading && (
                    <Grid size={{ xs: 1 }} className="d-flex align-items-center">
                        <CircularProgress
                            color="inherit"
                            sx={{
                                width: '1.6em !important',
                                height: '1.6em !important'
                            }}
                        />
                    </Grid>
                )} */}
            </Grid>

            {(files?.length > 0 || uploadedFiles?.length > 0) && (
                <>
                    {allowFileTypeSelection && (
                        <>
                            <Grid container className="file-table-header">
                                <Grid size={{ xs: isKYC ? 5 : 2 }}>
                                    <span>Document Name</span>
                                </Grid>

                                {!isKYC && (
                                    <>
                                        <Grid size={{ xs: 2 }}>
                                            <span>File Source</span>
                                        </Grid>

                                        <Grid size={{ xs: 2 }}>
                                            <span>Tab</span>
                                        </Grid>
                                    </>
                                )}

                                <Grid size={{ xs: isKYC ? 5 : 3 }}>
                                    <span>File Type</span>
                                </Grid>

                                {!isKYC && (
                                    <Grid size={{ xs: 2 }}>
                                        <span>Status</span>
                                    </Grid>
                                )}

                                <Grid size={{ xs: isKYC ? 2 : 1 }}>
                                    <span>Actions</span>
                                </Grid>
                            </Grid>

                            {isLoading && <LinearProgress />}
                        </>
                    )}

                    <div className="file-table-content-container" style={customStyle}>
                        {groupByFolder(files || [], file => file?.file?.folderName).map((group) => group.folderName ? (
                            <Fragment key={`saved:${group.folderName}`}>
                                {renderFolderRow(
                                    `saved:${group.folderName}`,
                                    group.folderName,
                                    group.entries.length,
                                    <span>{group.entries[0].item.file.fileType}</span>,
                                    folderDownloadData && (
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            aria-label="Download folder"
                                            disabled={isLoading}
                                            onClick={() => handleDownloadFolder(group.folderName)}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    )
                                )}

                                {expandedFolders.has(`saved:${group.folderName}`) && group.entries.map(({ item, index }) => renderSavedFileRow(item, index, true))}
                            </Fragment>
                        ) : (
                            renderSavedFileRow(group.item, group.index, false)
                        ))}

                        {groupByFolder(uploadedFiles || [], getFolderName).map((group) => group.folderName ? (
                            <Fragment key={`pending:${group.folderName}`}>
                                {renderFolderRow(
                                    `pending:${group.folderName}`,
                                    group.folderName,
                                    group.entries.length,
                                    <VirtualizedAutocomplete
                                        isMultiple={false}
                                        isObject={true}
                                        isRequired={true}
                                        isWritable={true}
                                        filterOn="File Type"
                                        options={kycFileTypes || []}
                                        selectedOptions={group.entries[0].item.docType || null}
                                        handleSelectChange={(filterOn, newValue) => handleFolderTypeChange(group.folderName, newValue)}
                                    />,
                                    <IconButton
                                        color="error"
                                        size="small"
                                        aria-label="Delete folder"
                                        disabled={!isWritable}
                                        onClick={() => handleDeleteNewFolder(group.folderName)}
                                    >
                                        <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                )}

                                {expandedFolders.has(`pending:${group.folderName}`) && group.entries.map(({ item, index }) => renderPendingFileRow(item, index, true))}
                            </Fragment>
                        ) : (
                            renderPendingFileRow(group.item, group.index, false)
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default FileUploader;
